import { ArrowLeft } from 'lucide-react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_web-pulse-4/artifacts/i5z1wfjq_WTFH%20logo.png';

export default function FunctionalSpec({ onBack, embedded }) {
  return (
    <div className={`${embedded ? 'bg-white text-gray-900' : 'min-h-screen bg-[#0d0d0d] text-white'} px-5 py-8 max-w-3xl mx-auto`} data-testid="functional-spec">
      {onBack && (
        <button onClick={onBack} className="flex items-center gap-2 text-white/60 hover:text-white mb-6 transition-colors" data-testid="spec-back-btn">
          <ArrowLeft size={18} /> Back
        </button>
      )}

      <img src={LOGO_URL} alt="WTFHappened" className="h-16 mb-6" />

      <h1 className="text-3xl font-black text-white mb-2">About WTFHappened</h1>
      <p className="text-white/50 text-sm mb-10">Last updated: April 2026</p>

      {/* Overview */}
      <Section title="Overview & Purpose">
        <p>
          WTFHappened is a news intelligence app that aggregates trending topics from 220+ sources — including live X/Twitter trends — then uses AI to break each story down into 3 simple cards so you can understand any headline in under 30 seconds.
        </p>
        <p>
          Instead of reading 10 articles to understand a story, WTFHappened gives you the <strong>What</strong>, the <strong>Why</strong>, and the <strong>How it affects You</strong> — powered by AI, refreshed daily at 05:00 GMT with a 3-layer deduplication pipeline and a 6-layer Trends Pipeline v2 that strip noise before it reaches your feed.
        </p>
        <p>
          The experience is organised around a <strong>Pulse Dashboard</strong> with three distinct tabs — <strong>News</strong>, <strong>Investor</strong>, and <strong>Reseller</strong> — each with nested filter rows for Region, Trend level, and Category. TikTok Shop, Depop, Vinted, StockX and other marketplaces are surfaced as <em>platforms</em> within the Reseller tab rather than as separate destinations.
        </p>
      </Section>

      {/* Audience */}
      <Section title="Who It Serves">
        <ul className="list-disc list-inside space-y-1.5">
          <li><strong>General readers</strong> — Anyone who wants to stay informed without doomscrolling</li>
          <li><strong>Casual investors</strong> — People tracking markets, crypto, forex, and economy trends for personal portfolio decisions</li>
          <li><strong>Resellers & traders</strong> — People who buy and sell sneakers, trading cards, fashion, collectibles, and other goods for profit — need to spot trends early</li>
        </ul>
      </Section>

      {/* What it provides */}
      <Section title="What It Provides">
        <ul className="list-disc list-inside space-y-1.5">
          <li>Aggregated trending topics from 220+ news, API, and marketplace sources</li>
          <li>Live X/Twitter trending topics pulled daily from US, UK, and India via the v2 API</li>
          <li>AI-generated 3-card explanations for every topic (What / Why / You)</li>
          <li><strong>Pulse Dashboard</strong> with 3 tabs — News, Investor, Reseller — each with nested Region / Trend / Category filter rows; TikTok Shop, Depop, Vinted and StockX surface as platform badges inside Reseller</li>
          <li>Personalised feed ranking powered by <strong>AWS OpenSearch</strong> vector scoring — falls back to MongoDB if unavailable</li>
          <li>Strict region tagging (<strong>US / GB / IN</strong>) enforced at every ingestion point — any untagged record is rejected</li>
          <li>Content-type gating: every topic tagged <code>news</code> or <code>premium</code> at ingestion; the News tab only shows true news</li>
          <li>3-layer deduplication pipeline: exact title matching, k-NN embedding similarity, and grey-zone logging</li>
          <li>6-layer <strong>Trends Pipeline v2</strong>: ingestion → feature extraction → MiniLM embeddings → vector matching → score engine → AI-written cards (see <em>Under the Hood</em> for a plain-English walkthrough)</li>
          <li>Premium marketplace trend signals scraped from resale platforms</li>
          <li>Daily WhatsApp digest with your personalised top stories</li>
          <li>Clickable source attribution on every feed card linking back to the original article</li>
          <li><strong>Single-session OTP login</strong> — every sign-in invalidates older sessions; admins bypass OTP via password</li>
        </ul>
      </Section>

      {/* Categories */}
      <Section title="Categories">
        <Subsection title="Free Categories">
          <PillList items={['YOUR NEWS', 'ALL', 'WORLD', 'TECH', 'SCIENCE', 'POLITICS', 'CULTURE', 'CELEB', 'STYLE', 'SPORTS', 'AI']} color="green" />
        </Subsection>
        <Subsection title="Premium — Investor Categories">
          <PillList items={['CRYPTO', 'MARKETS', 'ECONOMY', 'STOCKS', 'OPTIONS', 'SPORTS BETS', 'FANTASY', 'E-COMMERCE', 'TRENDS', 'FOREX', 'COMMODITIES', 'PROPERTY', 'BIZ']} color="amber" />
        </Subsection>
        <Subsection title="Premium — Reseller & Trader Categories">
          <PillList items={['SNEAKERS & STREETWEAR', 'FASHION & LUXURY', 'TRADING CARDS', 'GAMING ITEMS & SKINS', 'COLLECTIBLES & MEMORABILIA', 'TOYS & POP CULTURE', 'FLIPS']} color="purple" />
        </Subsection>
      </Section>

      {/* What is a Card */}
      <Section title="What Is a Card?">
        <p>Every trending topic is explained using 3 swipeable cards:</p>
        <div className="space-y-4 mt-4">
          <CardBox num="1" title="WHAT" color="#3B82F6"
            desc="Snap clarity — what actually happened, stripped of noise. A punchy hook + 3 key points." />
          <CardBox num="2" title="WHY" color="#F59E0B"
            desc="The hidden driver — why this matters, what's really going on beneath the headline. Surprising reframe + 3 insights." />
          <CardBox num="3" title="YOU" color="#10B981"
            desc="Personal hook — how it affects you specifically, based on your profession and location. Impacts + concrete actions you can take." />
        </div>
        <p className="mt-4 text-sm text-white/50">
          Premium subscribers also receive a personalised AI overlay on the YOU card, tailored to their profession, interests, and location.
        </p>
      </Section>

      {/* How far back */}
      <Section title="How Far Back Does Information Go?">
        <p>
          All ingestion — RSS feeds, API sources, marketplace scrapers, Google Trends, and X/Twitter trends — runs on a <strong>consolidated daily schedule at 05:00 GMT</strong>. The feed shows stories from the <strong>last 2 days</strong>. Older topics decay in ranking and are archived after 2 days.
        </p>
      </Section>

      <Divider />

      {/* Architecture */}
      <Section title="Under the Hood">
        <Subsection title="Pulse Dashboard — 3-Tab Structure">
          <p className="text-sm text-white/70 mb-2">
            The main surface is a 3-tab dashboard, each backed by its own data collection and filter controls:
          </p>
          <ul className="list-none space-y-2 ml-1">
            <li className="text-sm text-white/70"><span className="font-bold text-red-400">News</span> — served from <code>topics</code> where <code>content_type = "news"</code>. Region-specific.</li>
            <li className="text-sm text-white/70"><span className="font-bold text-blue-400">Investor</span> — served from <code>scored_entities</code> where <code>persona = "investor"</code> (tickers, crypto, forex, commodities).</li>
            <li className="text-sm text-white/70"><span className="font-bold text-orange-400">Reseller</span> — served from <code>scored_entities</code> (persona=reseller) <em>unioned with</em> <code>scored_products</code> (TikTok Shop product signals). Each card shows a platform badge (TikTok Shop, Depop, Vinted, StockX, eBay, etc.) and a Platform dropdown filter narrows results to a single marketplace.</li>
          </ul>
          <p className="text-sm text-white/70 mt-3">
            Inside each tab, users can drill down using nested filter rows: <strong>Region</strong> (US / UK / IN) → <strong>Trend level</strong> (rising, hot, peaking) → <strong>Category</strong> (category pills specific to the active tab) → <strong>Platform</strong> (Reseller only).
          </p>
        </Subsection>

        <Subsection title="Trends Pipeline v2 — 6 Layers">
          <p className="text-sm text-white/70 mb-3">
            Every premium signal passes through a 6-layer pipeline every night before anyone sees it. Each layer below is shown twice — once in <strong className="text-emerald-400">plain English</strong> (what it does for you and why it's worth doing), and once in <strong className="text-cyan-400">engineering detail</strong> (the actual tool and method used).
          </p>

          <PipelineLayer
            num="1" colour="#22D3EE" title="Ingestion — collect the raw material"
            plain="We go out to about 60 sources every night — news sites, Twitter/X, Reddit, Apify marketplace scrapers (eBay, Vinted, StockX, TikTok Shop), stock/crypto price feeds, sports betting lines — and pull everything that changed in the last 24 hours. Before a single item is saved we stamp it with a region (US/GB/IN, or GLOBAL for things like Bitcoin). Anything we can't place into a region is thrown away — that's how we keep the News tab's country filter honest."
            why="Without strict region tagging at the door, your News tab would leak foreign stories into every country filter (this was the P0 bug we fixed in iter 42)."
            tool={<><b>feedparser</b> for RSS, <b>httpx async</b> for REST APIs (CoinGecko, Yahoo Finance, FPL, X v2 trends), <b>Apify actors</b> for marketplaces, and 5 ingesters running in parallel via <code>asyncio.gather</code>.</>}
          />

          <PipelineLayer
            num="2" colour="#34D399" title="Feature extraction — turn articles into signals"
            plain="Each raw article gets reduced into a 'signal' — just the entity name (e.g. 'Nvidia', 'Bitcoin', 'Taylor Swift'), what happened to it ('price +8%', 'mentioned 12 times today', 'sold out'), its category, and a one-line summary called the card hook. Think of this as converting messy long-form articles into tidy little structured facts we can score later."
            why="Most downstream logic (scoring, dedup, ranking) is 10× cheaper when you're comparing tidy one-line signals instead of full articles."
            tool={<>A pre-compiled <b>regex alias dictionary</b> (~1,500 tickers, brands, people, teams, crypto symbols) picks the entity. <b>GPT-4.1-mini</b> writes the one-line hook — one LLM call per document, 50 in flight at a time via <code>asyncio.Semaphore(50)</code>.</>}
          />

          <PipelineLayer
            num="3" colour="#A78BFA" title="Embeddings — give every signal a coordinate in meaning-space"
            plain="Every signal text gets turned into 384 numbers (a 'vector') that encode its meaning. Two signals that say roughly the same thing end up with nearby vectors. This is how we'll spot that 'Apple beats earnings' and 'AAPL Q4 smashes Wall St. expectations' are really the same story — regardless of the words."
            why="Words lie; meaning doesn't. Matching by meaning (vectors) catches 40%+ more duplicates than matching by exact title."
            tool={<>Local <b>sentence-transformers</b> <code>all-MiniLM-L6-v2</code> running on CPU — no external API call, zero cost per vector. (We replaced Emergent's embedding endpoint here in iter 43 because their LLM proxy doesn't route <code>/embeddings</code>.)</>}
          />

          <PipelineLayer
            num="4" colour="#F59E0B" title="Vector matching — group aliases into one entity"
            plain="For each signal, we ask OpenSearch: 'Is there already a canonical entity that means the same thing?' If yes, we attach this signal to it (so Nvidia/NVDA/'nvidia corp' all roll up into one card). If no, we create a new canonical entity. This is the step that stops the feed being a noisy mess of near-duplicates."
            why="The feed would otherwise show 8 copies of the same Bitcoin story — one per source — instead of one card that says '8 sources, direction bullish'."
            tool={<><b>AWS OpenSearch 3.x</b> with a <b>faiss HNSW</b> kNN index (we moved off nmslib in iter 45 — deprecated in OS 3.0+). The <code>opensearch-py</code> client is blocking sync, so we wrap each call in <code>asyncio.to_thread()</code> — that gave us a 20× speedup in iter 46 (11.8 min → 35 s for 2,354 signals).</>}
          />

          <PipelineLayer
            num="5" colour="#EF4444" title="Score engine — how hot is this really?"
            plain="Each canonical entity gets a hotness score from 0 to 100 based on: how many different sources mentioned it, how fast the mentions are accelerating, whether prices/volumes confirm the story, and whether everyone's pointing in the same direction. The output is a score + a label ('breaking / rising / steady / cooling') + a fresh AI-written 3-tab card explaining what investors and resellers should make of it."
            why="A raw count of mentions is gameable and misleading. A weighted score that combines editorial velocity + structured price data + source diversity is much harder to fake and maps better to 'should I care?'"
            tool={<>Deterministic weighted maths for the numeric score (<code>HOTNESS_WEIGHTS</code>). <b>GPT-4.1-mini</b> for the card content — primary path is the direct OpenAI SDK (≈500 rpm), falling back to <b>Emergent LLM key</b> on 401/429/5xx (iter 47b dispatcher). All entities scored concurrently via <code>asyncio.gather(sem=50)</code>.</>}
          />

          <PipelineLayer
            num="6" colour="#10B981" title="Output — ship it to the feed"
            plain="We write the final topics + scored entities to MongoDB, push the topics into OpenSearch so the Dashboard can search/filter them instantly, then pre-generate the top 40 card explanations so that when you tap a card, it opens in under 200 ms — the card is already sitting in the cache."
            why="Cold LLM generation on tap would mean 2-4 s staring at a spinner. Pre-gen reduces perceived latency to near-zero and also decouples the app from LLM provider uptime."
            tool={<><b>Motor</b> async MongoDB bulk writes; <b>APScheduler</b> chains <code>pre_generate_top_cards()</code> right after the pipeline finishes.</>}
          />

          <p className="text-sm text-white/60 mt-4 italic">
            Pipeline code lives under <code>backend/services/trends/</code> (models, ingestion, embeddings, vector_matching, score_engine, pipeline). Measured end-to-end runtime on the full 2-day refresh is currently <strong className="text-white">~28 min</strong> — down from ~100 min pre-iter 45 (see Admin → Pipeline Runs for the live bottleneck breakdown).
          </p>
        </Subsection>

        <Subsection title="Resilience & Persistent Error Log (iter 52)">
          <p className="text-sm text-white/70 mb-2">
            Every pipeline failure (CoinGecko 429, Yahoo 401, OpenSearch timeout, pregen LLM exhaustion) is now persisted to <code>db.pipeline_errors</code> with a 14-day TTL — so morning standup can review what broke overnight even if the pod restarted in between.
          </p>
          <ul className="list-none space-y-1.5 ml-1 text-sm text-white/70">
            <li>• Helper <code>services/pipeline_error_log.log_pipeline_error()</code> — never raises; failures to persist are swallowed silently.</li>
            <li>• Wired into 10 error paths: scheduler, scheduler_ingest (5 ingest crons), investor_data_collector (CoinGecko + Yahoo stocks + Yahoo forex), pulse_pipeline (3 sub-pipelines + investor-empty critical), trends v2 pipeline.</li>
            <li>• <strong>Per-provider retry pattern:</strong> <code>_with_retry</code> attempts 3 times with 2 s / 5 s / 10 s backoff before logging exhaustion. Investor pipeline uses <code>asyncio.gather</code> across providers with <strong>partial-success</strong> mode — if CoinGecko 429s but Yahoo returns 7 assets, the pipeline ships 7 (pre-iter 52 it would have shipped 0).</li>
            <li>• Admin tab <strong>Pipeline Errors</strong> — filter by pipeline / source / severity / time-window; top-10 worst sources chip cloud; expandable rows for context JSON + stacktrace.</li>
          </ul>
        </Subsection>

        <Subsection title="Two-Stage Cold-Start Catch-Up (iter 53)">
          <p className="text-sm text-white/70 mb-2">
            APScheduler only fires CronTriggers going forward. If the backend pod cold-starts after 05:00 UTC (typical for preview env that recycles overnight), the morning ingest crons silently miss. The catch-up gate now auto-recovers per-job:
          </p>
          <ul className="list-none space-y-1.5 ml-1 text-sm text-white/70">
            <li>• For each of 9 daily crons, compute the most-recent scheduled UTC time (today if passed, else yesterday) and fire if <code>last_run</code> &lt; that.</li>
            <li>• <strong>Stage A — parallel</strong> via <code>asyncio.gather</code>: the 5 independent ingest jobs (<code>trending_data, google_trends, marketplace, trend_signals, tiktok_shop</code>). Wall-clock collapses from sum-of-durations to max-of-durations.</li>
            <li>• <strong>Stage B — sequential:</strong> <code>pregen → auto_publish → pulse_pipeline → trends_v2</code>. Each reads from <code>topics</code> populated by Stage A and would compete for the OpenAI rate-limit budget if run together.</li>
            <li>• <strong>Live verified April 25 cold-start:</strong> after 17-h overnight pod gap, Stage A finished in 117 s (vs. ~17 min sequential — 9× speedup). Total catch-up dropped from ~75 min to ~18 min.</li>
          </ul>
        </Subsection>

        <Subsection title="News Pipeline LLM Fan-Out (iter 54)">
          <p className="text-sm text-white/70 mb-2">
            Pre-iter 54 the Pulse news_pipeline scored ~250 news topics through an LLM-generated card per topic in batches of 5 sequentially via Emergent's ~60 RPM tier. Result: 605 s wall-clock = 99.9% of pulse runtime.
          </p>
          <ul className="list-none space-y-1.5 ml-1 text-sm text-white/70">
            <li>• <code>news_card_generator._call_llm</code> now delegates to <code>ai_engine._call_llm</code> → OpenAI tier-1 direct path (500 RPM / 200 k TPM) with automatic Emergent fallback on 401/429.</li>
            <li>• <code>news_pipeline.run_news_pipeline</code> replaced batches-of-5 loop with single <code>asyncio.gather</code> under <code>Semaphore(40)</code>.</li>
            <li>• Per-card timeout 45 s; failures degrade to fallback dict + write to pipeline_errors with topic_id + category context.</li>
            <li>• <strong>Live verified April 25:</strong> 249/249 cards generated in 19.5 s standalone — <strong className="text-white">30× speedup</strong>. Pulse pipeline dropped from 605 s to 86 s.</li>
          </ul>
        </Subsection>

        <Subsection title="X (Twitter) Auto-Publish (iter 53)">
          <p className="text-sm text-white/70 mb-2">
            Daily 05:30 UTC cron picks the 2 hottest fresh cards (<code>trend_score &ge; 70</code>, has AI 3-card explanation, never published before) and tweets them from <strong className="text-cyan-400">@WTFHappened2026</strong> as free organic distribution.
          </p>
          <ul className="list-none space-y-1.5 ml-1 text-sm text-white/70">
            <li>• Tweet format: <em>"WTF just happened? &lt;title&gt;"</em> + What/Why/You first 80 chars each + <code>#WTFHappened #Explained</code>.</li>
            <li>• Auth via OAuth 1.0a HMAC-SHA256 (X v2 write API). All 4 creds in <code>backend/.env</code>: API Key/Secret + Access Token/Secret. Access Token must be regenerated AFTER setting App permissions = "Read and write" — tokens minted under Read-only silently fail with HTTP 403.</li>
            <li>• Idempotency: each successful publish records to <code>db.published_cards</code>; the picker excludes already-published topic_ids so the same card never tweets twice.</li>
            <li>• Capacity: 2 cards/day × 365 = ~730 tweets/year of organic distribution at zero marginal cost.</li>
          </ul>
        </Subsection>

        <Subsection title="Scheduler Architecture (Split)">
          <p className="text-sm text-white/70 mb-2">
            Background jobs are split across three files to keep each concern small and testable:
          </p>
          <ul className="list-none space-y-2 ml-1">
            <li className="text-sm text-white/70"><span className="font-bold text-emerald-400">scheduler.py</span> — APScheduler core, job registration, and pipeline orchestration (daily 05:00 GMT run).</li>
            <li className="text-sm text-white/70"><span className="font-bold text-emerald-400">scheduler_ingest.py</span> — All source-specific ingestion methods (RSS, Google News, X/Twitter v2, Reddit, Apify scrapers).</li>
            <li className="text-sm text-white/70"><span className="font-bold text-emerald-400">scheduler_maintenance.py</span> — Cleanup jobs: MongoDB TTL enforcement, OpenSearch index sync, and dedup grey-zone pruning.</li>
          </ul>
          <p className="text-sm text-white/50 mt-2">AI pre-generation calls use hard timeouts so a slow upstream never blocks the scheduler cycle.</p>
        </Subsection>

        <Subsection title="Region Enforcement">
          <p className="text-sm text-white/70">
            Every ingestion path tags topics with one of <code>US</code>, <code>GB</code>, or <code>IN</code>. Records missing a region are rejected at insert time — there are no "global" fallback topics in the news collection. This keeps the News tab's country filter deterministic and lets us compute exact per-country volumetrics in the Admin Data Volume panel.
          </p>
        </Subsection>

        <Subsection title="Content Gating (News vs Premium)">
          <p className="text-sm text-white/70">
            Each topic carries a <code>content_type</code> field set at ingestion: <code>news</code> for free/editorial sources and <code>premium</code> for investor/reseller signals. The feed API accepts a <code>content_type</code> query parameter, so the News tab cannot accidentally leak premium topics and premium tabs cannot be diluted by free news.
          </p>
        </Subsection>

        <Subsection title="Authentication — Single-Session OTP">
          <p className="text-sm text-white/70">
            All user logins are gated by a 6-digit OTP delivered via <strong>Resend</strong> email. On successful OTP verification, the backend mints a fresh <code>session_token</code>, writes it to <code>users.active_session</code>, and invalidates any previously active session — so a given account can only be signed in on one device at a time. The admin account (<code>admin@wtfhappened.app</code>) bypasses the OTP step and authenticates via password directly, since ops scripts run against admin-only endpoints.
          </p>
        </Subsection>

        <Subsection title="AWS OpenSearch — Personalised Ranking">
          <p className="text-sm text-white/70 mb-2">
            The "Your News" personalised feed is ranked using AWS OpenSearch's <strong>function_score</strong> query engine. Each topic is indexed with a 384-dimension vector embedding of its title, plus metadata (category, region, trend score). When you open "Your News", the system builds a scoring function from your onboarding preferences (interests, profession, location) and returns results ranked by relevance — not just recency.
          </p>
          <p className="text-sm text-white/50">If OpenSearch is temporarily unavailable, the feed falls back seamlessly to MongoDB-based ranking.</p>
        </Subsection>

        <Subsection title="3-Layer Deduplication Pipeline">
          <p className="text-sm text-white/70 mb-2">
            Before any topic reaches your feed, it passes through a 3-layer dedup engine that runs during each ingestion cycle:
          </p>
          <ul className="list-none space-y-2 ml-1">
            <li className="text-sm text-white/70">
              <span className="font-bold text-blue-400">Layer 1 — Exact Match:</span> Normalised title comparison to catch identical headlines from different sources.
            </li>
            <li className="text-sm text-white/70">
              <span className="font-bold text-amber-400">Layer 2 — Embedding Similarity:</span> Each title is embedded using a local sentence-transformer model (all-MiniLM-L6-v2) and compared via k-NN cosine similarity in OpenSearch. Titles above 0.8 similarity are merged into the higher-scoring existing topic.
            </li>
            <li className="text-sm text-white/70">
              <span className="font-bold text-purple-400">Layer 3 — Grey Zone Logging:</span> Titles scoring between 0.6 and 0.8 are flagged and logged for review but still inserted. This data feeds future clustering improvements.
            </li>
          </ul>
          <p className="text-sm text-white/50 mt-2">Daily deduplication metrics are tracked in the Admin console.</p>
        </Subsection>

        <Subsection title="X/Twitter Trends Integration">
          <p className="text-sm text-white/70">
            WTFHappened connects to the X (formerly Twitter) Developer API v2 to pull real-time trending topics from the US, UK, and India. These are ingested daily alongside RSS and marketplace data, deduplicated through the same 3-layer pipeline, and surfaced in relevant categories across your feed.
          </p>
        </Subsection>

        <Subsection title="AI Pre-Generation">
          <p className="text-sm text-white/70">
            AI explanations for the top trending topics are pre-generated during the 05:00 GMT scheduled run — not on-demand — so cards load instantly when you open them. Each pre-gen call has a hard timeout so one slow LLM response cannot stall the whole scheduler cycle.
          </p>
        </Subsection>

        <Subsection title="Billing (Stripe)">
          <p className="text-sm text-white/70">
            Premium subscriptions are recurring Stripe Checkout sessions with <strong>no trial period</strong> — the card is charged immediately on subscribe. Cancellation is handled through the standard Stripe customer portal.
          </p>
        </Subsection>
      </Section>

      <Divider />

      {/* FREE */}
      <h2 className="text-2xl font-black text-green-400 mb-4 tracking-wider">FREE</h2>

      <Section title="What Does Free Get You?">
        <ul className="list-disc list-inside space-y-1.5">
          <li>Full access to 10 free categories (World, Tech, Politics, Culture, Celeb, Style, Sports, AI, Your News, All)</li>
          <li>AI-generated Card 1 (WHAT) for every topic — including premium topics</li>
          <li>Personalised feed ranking (Layer 1) based on your onboarding interests</li>
          <li>Region filtering (US, UK, India, Canada, Australia, UAE)</li>
          <li>Save and react to topics</li>
        </ul>
      </Section>

      <Section title="Free Data Sources">
        <Subsection title="World News">
          <SourceList items={['Reuters', 'BBC News', 'The Guardian', 'Al Jazeera', 'New York Times', 'Wall Street Journal', 'Associated Press', 'NPR', 'Axios', 'Sky News', 'The Independent', 'The Telegraph', 'ITV News', 'Channel 4 News', 'The Economist', 'Evening Standard', 'The i', 'Scroll.in', 'Firstpost', 'The Wire', 'Times of India', 'The Hindu', 'NDTV', 'Indian Express', 'Hindustan Times', 'CBC Canada', 'Global News Canada', 'National Post', 'ABC Australia', 'Sydney Morning Herald', '9News Australia']} />
        </Subsection>
        <Subsection title="Tech (includes Science)">
          <SourceList items={['TechCrunch', 'The Verge', 'Wired', 'Ars Technica', 'MIT Technology Review', 'BBC Tech', 'VentureBeat', 'Product Hunt', 'Sifted', 'UKTN', 'Tech Monitor', 'Inc42', 'YourStory', 'Entrackr', 'Apple App Store (top charts)', 'Google Play Store (top charts)', 'BBC Science', 'Scientific American', 'NASA', 'ISRO', 'Down To Earth', 'New Scientist', 'Nature News', 'The Guardian Science', 'Science Daily', 'The Hindu Science']} />
        </Subsection>
        <Subsection title="Politics">
          <SourceList items={['BBC Politics', 'Politico', 'The Hill', 'PoliticsHome', 'UK Parliament', 'GOV.UK', 'NDTV Politics', 'The Hindu Politics', 'Indian Express Political Pulse', 'Scroll.in Politics', 'The Wire Politics']} />
        </Subsection>
        <Subsection title="Finance">
          <SourceList items={['CNBC', 'MarketWatch', 'Financial Times', 'BBC Business', 'Economic Times', 'Moneycontrol', 'LiveMint', 'Business Standard', 'Seeking Alpha', 'City A.M.', 'This is Money']} />
        </Subsection>
        <Subsection title="Economy">
          <SourceList items={['Federal Reserve', 'Bank of England', 'PRS India', 'PIB India', 'LinkedIn Jobs Trends', 'Indeed Trends', 'Reed UK Jobs', 'Naukri India']} />
        </Subsection>
        <Subsection title="Entertainment & Celebrity">
          <SourceList items={['TMZ', 'People', 'E! News', 'Rolling Stone', 'Variety', 'Hollywood Reporter', 'Deadline', 'Vulture', 'NME', 'Radio Times', 'Film Companion', 'Bollywood Hungama', 'IMDb Trending', 'Rotten Tomatoes']} />
        </Subsection>
        <Subsection title="Culture & Lifestyle">
          <SourceList items={['BuzzFeed', 'Mashable', 'Vice', 'Know Your Meme', 'LADbible', 'UNILAD', 'Joe.co.uk', 'ScoopWhoop', 'Vogue', 'Time Out London', 'YouTube Trending', 'Instagram Trends', 'Reddit Rising', 'Discord Trends', 'Booking.com Trends', 'Airbnb Trends']} />
        </Subsection>
        <Subsection title="Sports">
          <SourceList items={['BBC Sport', 'ESPN', 'Sky Sports', 'Bleacher Report', 'Yahoo Sports', 'The Athletic UK', 'Cricbuzz', 'ESPN Cricinfo', 'NDTV Sports']} />
        </Subsection>
        <Subsection title="AI">
          <SourceList items={['Google News AI', 'VentureBeat AI', 'MIT Tech Review AI', 'The Register AI', 'Sifted AI', 'Inc42 AI', 'Analytics India Magazine']} />
        </Subsection>
        <Subsection title="Regional News Aggregators">
          <SourceList items={['Google News (US, UK, India, Canada, Australia)', 'Google Trends (US, UK, India, Canada, Australia)']} />
        </Subsection>
        <Subsection title="APIs">
          <SourceList items={['Wikipedia (most viewed articles)', 'Hacker News (top stories)', 'CoinGecko (trending coins)', 'X/Twitter API v2 (trending topics — US, UK, India)']} />
        </Subsection>
      </Section>

      <Divider />

      {/* PREMIUM */}
      <h2 className="text-2xl font-black text-amber-400 mb-4 tracking-wider">PREMIUM</h2>

      <Section title="Who Is Premium For?">
        <p>Premium serves two distinct use cases:</p>
        <div className="space-y-4 mt-4">
          <div className="border border-white/20 rounded-2xl p-5">
            <h4 className="font-bold text-white mb-2">1. Casual Investors</h4>
            <p className="text-sm">
              People tracking stocks, crypto, forex, commodities, and economic policy for personal investment decisions. Get AI-explained market moves, central bank actions, and earnings surprises — broken down so anyone can understand them.
            </p>
          </div>
          <div className="border border-white/20 rounded-2xl p-5">
            <h4 className="font-bold text-white mb-2">2. Resellers & Traders</h4>
            <p className="text-sm">
              People who buy and sell physical goods for profit — sneakers, trading cards, designer bags, collectibles, gaming items. Get early trend signals from marketplace platforms so you can spot what's heating up before the crowd.
            </p>
          </div>
        </div>
      </Section>

      <Section title="What Types of Trends?">
        <ul className="list-disc list-inside space-y-1.5">
          <li><strong>Market trends</strong> — Stock movements, options flow, earnings surprises, IPOs</li>
          <li><strong>Crypto signals</strong> — Token surges, DeFi developments, regulatory moves</li>
          <li><strong>Forex & macro</strong> — Currency pairs, central bank decisions, interest rate changes</li>
          <li><strong>Commodity shifts</strong> — Oil, gold, energy policy changes</li>
          <li><strong>Resale demand signals</strong> — What's selling fast, price spikes, upcoming drops</li>
          <li><strong>Marketplace intelligence</strong> — Brand concentration, average pricing, volume trends from live marketplace data</li>
        </ul>
      </Section>

      <Section title="Premium Data Sources — Investors">
        <Subsection title="Stocks & Options">
          <SourceList items={['Yahoo Finance', 'MarketWatch Top', 'Benzinga', 'Google News Finance']} />
        </Subsection>
        <Subsection title="Crypto">
          <SourceList items={['CoinDesk', 'Cointelegraph', 'CoinGecko API']} />
        </Subsection>
        <Subsection title="Forex & Macro">
          <SourceList items={['ForexLive', 'Investing.com', 'European Central Bank (ECB)']} />
        </Subsection>
        <Subsection title="Commodities">
          <SourceList items={['OilPrice.com', 'World Gold Council', 'U.S. Energy Information Administration (EIA)', 'Bloomberg Commodities']} />
        </Subsection>
        <Subsection title="Property">
          <SourceList items={['Redfin', 'Realtor.com', 'HousingWire', 'PropertyWire', 'Zillow (US trends)', 'Rightmove (UK trends)', '99acres (India trends)']} />
        </Subsection>
        <Subsection title="Sneakers & Streetwear">
          <SourceList items={['StockX (Apify)', 'Sneaker News', 'Highsnobiety', 'Google News Sneakers']} />
        </Subsection>
        <Subsection title="Sports Betting">
          <SourceList items={['Google News Betting']} />
        </Subsection>
        <Subsection title="Traders & Resellers">
          <SourceList items={['Google News Fantasy Sports (US)', 'CricTracker Fantasy Tips (India)', 'Google News Fantasy Cricket (India)', 'FPL Transfer Trends API — most transferred in/out, price risers (UK)', 'ESPN Fantasy Trends — most added/dropped players (US)', 'Yahoo Fantasy Buzz — trending picks and analysis (US)', 'Cricbuzz Fantasy — playing XIs, match predictions (India)']} />
        </Subsection>
      </Section>

      <Section title="Premium Data Sources — Traders & Resellers">
        <Subsection title="E-commerce & Retail Trends">
          <SourceList items={['Amazon (Movers & Shakers)', 'Flipkart (trending)', 'Walmart (bestsellers)', 'Argos UK (trending)', 'Etsy (trending)', 'AliExpress (popular)', 'Shopify Trends', 'Google News E-commerce', 'Google News Trends']} />
        </Subsection>
        <Subsection title="Sneakers & Streetwear">
          <SourceList items={['GOAT (Apify)', 'Depop (Apify)', 'Google News Sneakers']} />
        </Subsection>
        <Subsection title="Fashion & Luxury">
          <SourceList items={['Fashionista', 'PurseBlog', 'Google News Luxury', 'Vestiaire Collective (Apify)', 'Vinted (Apify)']} />
        </Subsection>
        <Subsection title="Trading Cards">
          <SourceList items={['Beckett News', 'Google News Cards', 'Cardmarket (Apify)', 'TCGPlayer (Apify)', 'Card Ladder (Apify)']} />
        </Subsection>
        <Subsection title="Gaming Items & Skins">
          <SourceList items={['Google News Gaming', 'Steam Community Market (Apify)', 'PlayerAuctions']} />
        </Subsection>
        <Subsection title="Sports Betting">
          <SourceList items={['OddsChecker', 'Bet365 Signal']} />
        </Subsection>
        <Subsection title="Collectibles & Memorabilia">
          <SourceList items={['Google News Collectibles']} />
        </Subsection>
        <Subsection title="Toys & Pop Culture">
          <SourceList items={['Google News Toys']} />
        </Subsection>
        <Subsection title="Marketplace Platforms (Apify Scrapers)">
          <SourceList items={[
            'eBay (Browse API — pricing, brand concentration, watchlist trends)',
            'Vinted (catalog sorted by favourites = demand signal)',
            'Depop (trending searches, popular items)',
            'StockX (market insights, culture index)',
            'GOAT (top selling, editorial releases)',
            'Cardmarket (trending card data)',
            'TCGPlayer (top selling, price trends)',
            'Vestiaire Collective (editor picks, likes-based demand)',
            'TikTok Creative Center (trending hashtags, viral content topics)',
            'Craigslist (hot items for sale, regional demand signals)',
            'Steam Community Market (CS2, Dota 2, TF2 — trending items by volume)',
            'TrendHunter (consumer/tech/lifestyle macro trends)',
            'Card Ladder (trading card market indexes & movements)',
            'OLX (second-hand marketplace hot items & pricing)',
          ]} />
        </Subsection>
      </Section>

      {/* WhatsApp */}
      <Section title="WhatsApp Functionality">
        <ul className="list-disc list-inside space-y-1.5">
          <li>After subscribing, users are prompted to connect via WhatsApp</li>
          <li>On first message, a <strong>welcome message</strong> is sent along with the daily digest</li>
          <li>Each day, users send any message to receive a personalised <strong>5-story digest</strong></li>
          <li>The digest prioritises stories from the user's preferred categories (based on onboarding)</li>
          <li>Each story includes the category label, headline, and a direct link to the full 3-card explanation</li>
          <li>One digest per day — if already received, a reminder is sent instead</li>
        </ul>
      </Section>

      <Divider />

      {/* Trend Detection Strategy */}
      <h2 className="text-2xl font-black text-cyan-400 mb-4 tracking-wider">TREND DETECTION STRATEGY</h2>

      <Section title="Pipeline walkthrough — see above">
        <p className="text-sm text-white/70">
          The six layers of Trends Pipeline v2 are documented in full — plain-English, why it helps, and exact tool/method — in the <strong className="text-white">Under the Hood → Trends Pipeline v2</strong> section above. That's the single source of truth for how signals flow from ingestion to the scored-entities feed; we intentionally don't repeat it here.
        </p>
      </Section>

      <Section title="Category-Specific Keyword Taxonomy">
        <div className="space-y-3">
          <div className="flex gap-3">
            <span className="text-xs font-bold text-blue-400 w-24 shrink-0 pt-0.5">Investors</span>
            <p className="text-sm text-white/70">Tickers (AAPL, BTC), token symbols, macro indicators (CPI, Fed rate, PMI)</p>
          </div>
          <div className="flex gap-3">
            <span className="text-xs font-bold text-emerald-400 w-24 shrink-0 pt-0.5">Traders</span>
            <p className="text-sm text-white/70">SKU-level product names, brand + model combinations (Nike Dunk Low, Pokemon 151), set/series names for cards</p>
          </div>
          <div className="flex gap-3">
            <span className="text-xs font-bold text-amber-400 w-24 shrink-0 pt-0.5">Property</span>
            <p className="text-sm text-white/70">Postcodes, neighbourhoods, price-band movements (£200k-£300k bracket)</p>
          </div>
          <div className="flex gap-3">
            <span className="text-xs font-bold text-red-400 w-24 shrink-0 pt-0.5">Stopwords</span>
            <p className="text-sm text-white/70">Generic keywords like "market", "trends", "trading", "investing" are noise — excluded from entity extraction</p>
          </div>
        </div>
      </Section>

      <div className="h-20" />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="mb-10">
      <h3 className="text-xl font-bold text-white mb-3">{title}</h3>
      <div className="text-[15px] leading-relaxed text-white/80 space-y-3">{children}</div>
    </div>
  );
}

function Subsection({ title, children }) {
  return (
    <div className="mt-3">
      <h4 className="text-sm font-bold text-white/60 uppercase tracking-wider mb-2">{title}</h4>
      {children}
    </div>
  );
}

function PillList({ items, color }) {
  const colors = {
    green: 'bg-green-500/15 text-green-400 border-green-500/20',
    amber: 'bg-amber-500/15 text-amber-400 border-amber-500/20',
    purple: 'bg-purple-500/15 text-purple-400 border-purple-500/20',
  };
  return (
    <div className="flex flex-wrap gap-2">
      {items.map(item => (
        <span key={item} className={`px-3 py-1 rounded-full text-xs font-bold border ${colors[color]}`}>{item}</span>
      ))}
    </div>
  );
}

function SourceList({ items }) {
  return (
    <ul className="list-none space-y-1 ml-1">
      {items.map(item => (
        <li key={item} className="text-sm text-white/70 before:content-['·'] before:mr-2 before:text-white/30">{item}</li>
      ))}
    </ul>
  );
}

function CardBox({ num, title, color, desc }) {
  return (
    <div className="flex gap-4 items-start">
      <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 font-black text-white text-sm" style={{ background: color }}>
        {num}
      </div>
      <div>
        <h4 className="font-bold text-white">{title}</h4>
        <p className="text-sm text-white/60 mt-0.5">{desc}</p>
      </div>
    </div>
  );
}

function Divider() {
  return <hr className="border-white/10 my-10" />;
}

function PipelineLayer({ num, colour, title, plain, why, tool }) {
  return (
    <div className="mt-4 rounded-2xl border border-white/10 overflow-hidden">
      {/* Header — layer number + title in the layer's signature colour */}
      <div
        className="flex items-center gap-3 px-4 py-3"
        style={{
          background: `${colour}14`,
          borderLeft: `3px solid ${colour}`,
        }}
      >
        <div
          className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 font-black text-sm"
          style={{ background: colour, color: '#0d0d0d' }}
        >
          {num}
        </div>
        <h5 className="font-bold text-white text-[15px] leading-tight">{title}</h5>
      </div>

      <div className="px-4 py-4 space-y-3 bg-white/[0.02]">
        {/* Plain-English */}
        <div>
          <p className="text-[10px] font-bold text-emerald-400/80 tracking-widest uppercase mb-1">Plain english</p>
          <p className="text-sm text-white/80 leading-relaxed">{plain}</p>
        </div>

        {/* Why it matters */}
        <div className="pl-3 border-l-2 border-white/10">
          <p className="text-[10px] font-bold text-amber-400/70 tracking-widest uppercase mb-1">Why this helps</p>
          <p className="text-[13px] text-white/65 italic leading-relaxed">{why}</p>
        </div>

        {/* Engineering detail */}
        <div>
          <p className="text-[10px] font-bold text-cyan-400/80 tracking-widest uppercase mb-1">Tool &amp; method</p>
          <p className="text-[13px] text-white/70 leading-relaxed">{tool}</p>
        </div>
      </div>
    </div>
  );
}
